import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEO/SEOHead';
import axios from 'axios';

const WORDPRESS_API = 'https://www.storespergolas.com/blog/wp-json/wp/v2';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [categories, setCategories] = useState([]);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        // Récupérer l'article par slug
        const response = await axios.get(`${WORDPRESS_API}/posts`, {
          params: { slug, _embed: true }
        });

        if (response.data && response.data.length > 0) {
          const postData = response.data[0];
          setPost(postData);

          // Récupérer les catégories
          if (postData.categories && postData.categories.length > 0) {
            const categoriesResponse = await axios.get(
              `${WORDPRESS_API}/categories?include=${postData.categories.join(',')}`
            );
            setCategories(categoriesResponse.data);
          }

          // Récupérer l'auteur
          if (postData.author) {
            const authorResponse = await axios.get(`${WORDPRESS_API}/users/${postData.author}`);
            setAuthor(authorResponse.data);
          }
        }
      } catch (error) {
        console.error('Erreur chargement article:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const extractImageUrl = (post) => {
    if (post._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
      return post._embedded['wp:featuredmedia'][0].source_url;
    }
    return null;
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const estimateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const textContent = stripHtml(content);
    const wordCount = textContent.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min de lecture`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-celadon"></div>
          <p className="mt-4 text-gray-600">Chargement de l'article...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Article non trouvé</h1>
          <button
            onClick={() => navigate('/blog')}
            className="text-celadon hover:underline"
          >
            Retour au blog
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const featuredImage = extractImageUrl(post);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={stripHtml(post.title.rendered)}
        description={stripHtml(post.excerpt.rendered).substring(0, 160)}
        url={`/blog/${slug}`}
        image={featuredImage}
        type="article"
        breadcrumbs={[
          { name: 'Accueil', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: stripHtml(post.title.rendered), url: `/blog/${slug}` }
        ]}
      />

      <Header />

      {/* Bouton retour */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-celadon hover:text-celadon-light transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Retour au blog</span>
          </button>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Image mise en avant */}
          {featuredImage && (
            <div className="relative h-96 bg-gray-200">
              <img
                src={featuredImage}
                alt={stripHtml(post.title.rendered)}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Contenu */}
          <div className="p-8">
            {/* Catégories */}
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {categories.map((category) => (
                  <span
                    key={category.id}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-celadon/10 text-celadon rounded-full text-sm"
                  >
                    <Tag size={14} />
                    {category.name}
                  </span>
                ))}
              </div>
            )}

            {/* Titre */}
            <h1
              className="text-4xl font-bold text-gray-900 mb-4"
              dangerouslySetInnerHTML={{ __html: post.title.rendered }}
            />

            {/* Meta informations */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-8 pb-8 border-b">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>{new Date(post.date).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
              
              {author && (
                <div className="flex items-center gap-2">
                  <User size={16} />
                  <span>{author.name}</span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{estimateReadingTime(post.content.rendered)}</span>
              </div>
            </div>

            {/* Contenu de l'article */}
            <div
              className="prose prose-lg max-w-none
                prose-headings:text-gray-900 prose-headings:font-bold
                prose-h2:text-3xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-2xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-celadon prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900
                prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
                prose-li:text-gray-700 prose-li:mb-2
                prose-img:rounded-lg prose-img:shadow-md
                prose-blockquote:border-l-4 prose-blockquote:border-celadon prose-blockquote:pl-4 prose-blockquote:italic"
              dangerouslySetInnerHTML={{ __html: post.content.rendered }}
            />

            {/* Bouton retour en bas */}
            <div className="mt-12 pt-8 border-t">
              <button
                onClick={() => navigate('/blog')}
                className="inline-flex items-center gap-2 bg-celadon text-white px-6 py-3 rounded-lg hover:bg-celadon-light transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Retour au blog</span>
              </button>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
